import { FC } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next/pages';

import cn from 'classnames';

import { TranslationNamespace } from '@/i18n';
import { CheckOutlined } from '@ant-design/icons';

import fishplaceImage from '../../images/tariff_fishplace.webp';
import personImage from '../../images/tariff_person.webp';

import styles from './DelegationSection.module.scss';
interface DelegationCardProps {
	image: typeof personImage;
	label: string;
	tasks: string[];
	withBorder?: boolean;
}

const DelegationCard = ({ image, label, tasks, withBorder = false }: DelegationCardProps) => (
	<div
		className={cn(styles.card, {
			[styles.cardAccent]: withBorder,
		})}
	>
		<Image className={styles.cardImage} src={image} alt="Card" />
		<div className={styles.cardLabel}>{label}</div>
		<ul className={styles.taskList}>
			{tasks.map((task) => (
				<li className={styles.task} key={task}>
					<CheckOutlined className={styles.check} aria-hidden="true" />
					<span>{task}</span>
				</li>
			))}
		</ul>
	</div>
);

export const DelegationSection: FC = () => {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	const employeeTasks = ['negotiations', 'relations', 'sales', 'decisions'].map((key) =>
		t(`delegation.employee.${key}`),
	);
	const fishplaceTasks = [
		'applications',
		'retention',
		'control',
		'documents',
		'reports',
		'data',
	].map((key) => t(`delegation.fishplace.${key}`));

	return (
		<div className={styles.section}>
			<div className={styles.content}>
				<div className={styles.description}>
					<div className={styles.eyebrow}>{t('delegation.eyebrow')}</div>
					<h2 className={styles.title}>{t('delegation.title')}</h2>
					<p className={styles.lead}>{t('delegation.lead')}</p>
					<div className={styles.note}>{t('delegation.note')}</div>
					<Link className={styles.link} href="/calculator">
						{t('delegation.compare')}&nbsp;<span aria-hidden="true"> →</span>
					</Link>
				</div>
				<div className={styles.cards}>
					<DelegationCard
						image={personImage}
						label={t('delegation.employee-label')}
						tasks={employeeTasks}
					/>
					<DelegationCard
						image={fishplaceImage}
						label={t('delegation.fishplace-label')}
						tasks={fishplaceTasks}
						withBorder
					/>
				</div>
			</div>
		</div>
	);
};
